import * as XLSX from 'xlsx';

export interface ExportColumn {
  header: string;
  dataIndex: string;
  format?: 'currency' | 'percent' | 'number' | 'text' | 'boolean';
}

function formatCellValue(
  value: unknown,
  format?: ExportColumn['format']
): string | number {
  if (value === null || value === undefined) {
    return '';
  }

  switch (format) {
    case 'currency':
      return typeof value === 'number' ? value : Number(value) || 0;
    case 'percent':
      if (typeof value === 'number') {
        return `${value.toFixed(2)}%`;
      }
      return `${Number(value) || 0}%`;
    case 'number':
      return typeof value === 'number' ? value : Number(value) || 0;
    case 'boolean':
      return value === true || value === 'true' || value === 'Yes' ? 'Yes' : 'No';
    case 'text':
    default:
      if (value == null) return '';
      if (typeof value === 'object') return JSON.stringify(value);
      return String(value);
  }
}

export function exportToExcel<T>(
  data: T[],
  columns: ExportColumn[],
  filename: string
): void {
  // Create worksheet data with headers
  const headers = columns.map((col) => col.header);

  // Transform data rows
  const rows = data.map((row) =>
    columns.map((col) => formatCellValue((row as Record<string, unknown>)[col.dataIndex], col.format))
  );

  // Combine headers and rows
  const worksheetData = [headers, ...rows];

  // Create worksheet
  const ws = XLSX.utils.aoa_to_sheet(worksheetData);

  // Set column widths based on content
  const colWidths = columns.map((col, index) => {
    const maxLength = Math.max(
      col.header.length,
      ...rows.map((row) => String(row[index]).length)
    );
    return { wch: Math.min(Math.max(maxLength + 2, 10), 40) };
  });
  ws['!cols'] = colWidths;

  // Create workbook
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, ws, 'Data');

  // Generate and download file
  XLSX.writeFile(workbook, filename);
}

export interface ExportSheet<T> {
  name: string;
  data: T[];
  columns: ExportColumn[];
}

export function exportToExcelMultiSheet<T>(
  sheets: ExportSheet<T>[],
  filename: string
): void {
  const workbook = XLSX.utils.book_new();

  for (const sheet of sheets) {
    const headers = sheet.columns.map((col) => col.header);
    const rows = sheet.data.map((row) =>
      sheet.columns.map((col) =>
        formatCellValue((row as Record<string, unknown>)[col.dataIndex], col.format)
      )
    );
    const worksheetData = [headers, ...rows];
    const ws = XLSX.utils.aoa_to_sheet(worksheetData);

    const colWidths = sheet.columns.map((col, index) => {
      const maxLength = Math.max(
        col.header.length,
        ...rows.map((row) => String(row[index]).length)
      );
      return { wch: Math.min(Math.max(maxLength + 2, 10), 40) };
    });
    ws['!cols'] = colWidths;

    XLSX.utils.book_append_sheet(workbook, ws, sheet.name);
  }

  XLSX.writeFile(workbook, filename);
}
